/**
 * contactListFilterConfigs.ts
 *
 * Maps each data source type to its corresponding list page's FilterConfig[].
 * This is the "router" -- it imports from shared files and never defines
 * filter arrays itself, guaranteeing one source of truth.
 */
import type { FilterConfig } from '@/types/filters.types';
import { buildPlanFilterConfigs } from '@/pages/plansShared';
import { buildPatientFilterConfigs } from '@/pages/patientsShared';
import { APPOINTMENT_FILTER_CONFIGS } from '@/pages/appointmentsShared';
import { buildPaymentFilterConfigs } from '@/pages/payments/paymentsShared';
import { MEMBERSHIP_FILTER_CONFIGS } from '@/pages/memberships/membershipsManagementShared';
import { PACKAGE_FILTER_CONFIGS } from '@/pages/packages/packagesManagementShared';
import { INTEGRATION_ORDER_LIST_FILTER_CONFIGS } from '@/pages/marketplace/integrationOrdersShared';

export function getFilterConfigsForSource(
  sourceType: string,
  opts?: { doctorLabel?: string; coordLabel?: string },
): FilterConfig[] {
  switch (sourceType) {
    case 'plan':
    case 'inquiry':
      return buildPlanFilterConfigs(
        opts?.doctorLabel ?? 'MD',
        opts?.coordLabel ?? 'Coordinator',
      );
    case 'patient':
    case 'app_users':
      return buildPatientFilterConfigs(opts?.coordLabel ?? 'Coordinator');
    case 'membership':
    case 'member':
      return MEMBERSHIP_FILTER_CONFIGS;
    case 'appointment':
      return APPOINTMENT_FILTER_CONFIGS;
    case 'package':
      return PACKAGE_FILTER_CONFIGS;
    case 'payment':
      // Same builder the Payments page uses, so the Doctor / Coordinator
      // filters carry the account's own wording here too.
      //
      // NO defaultDatePreset, and never add one. A dynamic list must resolve
      // to exactly what was saved: ContactListDetailPage hydrates only the
      // keys present in filterConditions, so a list saved with no date range
      // would silently adopt whatever the config defaults to and start
      // resolving to that window -- quietly changing who is in the audience.
      // The Payments page passes 'today' at ITS call site for that reason.
      return buildPaymentFilterConfigs(
        opts?.doctorLabel ?? 'Doctor',
        opts?.coordLabel ?? 'Coordinator',
      );
    case 'integration_order':
      // Marketplace orders (SCRUM-2207): the Axiom / Partell pages' filters,
      // plus Vendor. No date default, as for payments.
      return INTEGRATION_ORDER_LIST_FILTER_CONFIGS;
    default:
      return buildPatientFilterConfigs(opts?.coordLabel ?? 'Coordinator');
  }
}
